import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Clock, Copy, Check, ShoppingBag, AlertTriangle } from 'lucide-react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const GiftCardsTab = ({ isMobile }) => {
  const { user } = useAuth();
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Fetch gift cards from Firebase
  useEffect(() => {
    if (user) {
      const giftCardsRef = ref(database, `users/${user.uid}/giftCards`);
      const unsubscribe = onValue(giftCardsRef, async (snapshot) => {
        if (snapshot.exists()) {
          const giftCardsData = snapshot.val();
          const cardsArray = [];
          
          // Fetch detailed gift card data
          for (const key in giftCardsData) {
            const giftCardId = giftCardsData[key].id;
            if (giftCardId) {
              try {
                const fullCardRef = ref(database, `giftCards/${giftCardId}`);
                const fullCardSnapshot = await new Promise((resolve) => {
                  onValue(fullCardRef, resolve, { onlyOnce: true });
                });
                
                if (fullCardSnapshot.exists()) {
                  const fullCardData = fullCardSnapshot.val();
                  cardsArray.push({
                    id: giftCardId,
                    ...fullCardData,
                    type: giftCardsData[key].type || 'purchased'
                  });
                }
              } catch (error) {
                console.error('Error fetching gift card details:', error);
              }
            }
          }
          
          // Sort by date (newest first)
          cardsArray.sort((a, b) => {
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
          
          setGiftCards(cardsArray);
        } else {
          setGiftCards([]);
        }
        setLoading(false);
      });
      
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [user]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };
  
  // Format gift card code with dashes
  const formatCode = (code) => {
    if (!code) return '';
    
    // Clean up any existing formatting
    const cleanCode = code.replace(/-/g, '').toUpperCase();
    
    // Format with dashes every 4 characters
    let formattedCode = '';
    for (let i = 0; i < cleanCode.length; i++) {
      if (i > 0 && i % 4 === 0) formattedCode += '-';
      formattedCode += cleanCode[i];
    }
    
    return formattedCode;
  };
  
  // Copy gift card code to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy code:', err);
      });
  };
  
  // Filter gift cards
  const filteredGiftCards = giftCards.filter(card => {
    const now = new Date();
    const expirationDate = new Date(card.expiresAt);
    const isExpired = expirationDate < now;
    const isActive = card.status === 'active' && card.balance > 0 && !isExpired;
    const isUsed = card.status === 'redeemed' || card.balance === 0;
    
    if (activeFilter === 'active') return isActive;
    if (activeFilter === 'used') return isUsed && !isExpired;
    if (activeFilter === 'expired') return isExpired;
    return true; // 'all' filter
  });
  
  // Check if a gift card is expired
  const isExpired = (card) => {
    const now = new Date();
    const expirationDate = new Date(card.expiresAt);
    return expirationDate < now;
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Your Gift Cards</h2>
        <Link to="/gift-cards">
          <button className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-4 py-2 rounded-lg text-sm font-medium">
            Purchase Gift Card
          </button>
        </Link>
      </div>
      
      {/* Filter Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeFilter === 'all'
              ? 'text-[#3A98B9] border-b-2 border-[#3A98B9]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveFilter('all')}
        >
          All
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeFilter === 'active'
              ? 'text-[#3A98B9] border-b-2 border-[#3A98B9]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveFilter('active')}
        >
          Active
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeFilter === 'used'
              ? 'text-[#3A98B9] border-b-2 border-[#3A98B9]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveFilter('used')}
        >
          Used
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            activeFilter === 'expired'
              ? 'text-[#3A98B9] border-b-2 border-[#3A98B9]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveFilter('expired')}
        >
          Expired
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3A98B9] mx-auto mb-2"></div>
          <p>Loading your gift cards...</p>
        </div>
      ) : filteredGiftCards.length > 0 ? (
        <div className="space-y-4">
          {filteredGiftCards.map(card => {
            const cardExpired = isExpired(card);
            const cardUsed = card.status === 'redeemed' || card.balance === 0;
            const isInactive = cardExpired || cardUsed;
            
            return (
              <div 
                key={card.id} 
                className={`border rounded-lg overflow-hidden ${
                  isInactive ? 'border-gray-200 opacity-75' : 'border-[#3A98B9]/50'
                }`}
              >
                <div className={`p-4 ${isInactive ? 'bg-gray-50' : 'bg-[#3A98B9]/5'}`}>
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        cardExpired 
                          ? 'bg-gray-100 text-gray-800' 
                          : cardUsed 
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                      }`}>
                        {cardExpired ? 'Expired' : cardUsed ? 'Used' : 'Active'}
                      </span>
                      <h3 className="ml-3 font-medium">
                        {card.type === 'purchased' ? 'Purchased Gift Card' : 'Received Gift Card'}
                      </h3>
                    </div>
                    <div className="text-sm text-gray-600">
                      Card ID: #{card.id.slice(-6).toUpperCase()}
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start">
                      <Gift className="h-5 w-5 text-[#3A98B9] mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Gift Card Amount</p>
                        <div className="flex items-baseline">
                          <span className={`text-xl font-bold ${isInactive ? 'text-gray-500' : 'text-[#3A98B9]'}`}>
                            ${card.balance.toFixed(2)}
                          </span>
                          {card.balance !== card.amount && (
                            <span className="text-sm text-gray-500 ml-2">
                              of ${card.amount.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-[#3A98B9] mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Valid Until</p>
                        <p className={`${cardExpired ? 'text-red-600' : 'text-gray-600'}`}>
                          {formatDate(card.expiresAt)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Created: {formatDate(card.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex flex-col">
                    <div className="mb-2 flex justify-between items-center">
                      <span className="text-sm text-gray-500">Gift Card Code</span>
                      <button
                        onClick={() => handleCopyCode(card.code)}
                        className="text-[#3A98B9] hover:text-[#2A87A8] text-sm flex items-center"
                        disabled={isInactive}
                      >
                        {copiedCode === card.code ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 mr-1" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-4 font-mono text-center">
                      {formatCode(card.code)}
                    </div>
                  </div>
                  
                  {card.recipientName && (
                    <div className="mt-1 text-sm text-gray-600">
                      Recipient: {card.recipientName}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                    {isInactive ? (
                      <div className="flex items-center text-gray-500 text-sm">
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        {cardExpired ? 'This gift card has expired' : 'This gift card has been used'}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        Use this code at checkout to redeem
                      </div>
                    )}
                    
                    {!isInactive && (
                      <Link to="/book">
                        <button className="px-3 py-1 bg-[#3A98B9] text-white rounded-md text-sm hover:bg-[#2A87A8] flex items-center">
                          <ShoppingBag className="h-4 w-4 mr-1" />
                          Book Now
                        </button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#3A98B9]/10 flex items-center justify-center mb-4">
            <Gift className="h-8 w-8 text-[#3A98B9]" />
          </div>
          <h3 className="text-xl font-medium text-gray-800 mb-2">No gift cards found</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            {activeFilter !== 'all' 
              ? `You don't have any ${activeFilter} gift cards.` 
              : "You don't have any gift cards yet. Purchase a gift card for someone or redeem one that was given to you."}
          </p>
          <Link to="/gift-cards">
            <button className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300">
              Purchase a Gift Card
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default GiftCardsTab;